import { useState, useEffect, useCallback } from "react";
import { Search, Loader, Users, CheckCircle, Send, Radio, Plus } from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";
import CreateGroupModal from "./CreateGroupModal";
import BroadcastBubble from "./BroadcastBubble";

const StepGroup = ({ selected, onSelect }) => {
  const [groups, setGroups]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState("");
  const [history, setHistory]         = useState([]);
  const [historyLoading, setHistLoading] = useState(false);
  const [showCreateModal, setShowCreate] = useState(false);

  const loadGroups = useCallback(() => {
    setLoading(true);
    api.get("/groups")
      .then((r) => setGroups(r.data.groups || r.data.data || []))
      .catch(() => toast.error("Failed to load groups"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  useEffect(() => {
    if (!selected) { setHistory([]); return; }
    setHistLoading(true);
    api.get(`/broadcasts?groupId=${selected.id}`)
      .then((r) => setHistory(r.data.data || []))
      .catch(() => setHistory([]))
      .finally(() => setHistLoading(false));
  }, [selected?.id]);

  const handleGroupCreated = (newGroup) => {
    setGroups((prev) => [newGroup, ...prev]);
    onSelect(newGroup);
    setShowCreate(false);
  };

  const filtered = groups.filter((g) =>
    g.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-0.5">Select Audience</h2>
          <p className="text-sm text-gray-500">Choose the customer group for this broadcast.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-colors flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          New Group
        </button>
      </div>

      {showCreateModal && (
        <CreateGroupModal
          onClose={() => setShowCreate(false)}
          onCreated={handleGroupCreated}
        />
      )}

      <div className="flex flex-col lg:flex-row gap-4">
        {/* ── Group picker ── */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 mb-3">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search groups…"
              className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No groups found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filtered.map((g) => (
                <button
                  key={g.id}
                  onClick={() => onSelect(g)}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${
                    selected?.id === g.id
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{g.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{g.description || "No description"}</p>
                    </div>
                    {selected?.id === g.id && <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />}
                  </div>
                  <div className="flex items-center gap-1.5 mt-2">
                    <Users className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-500">
                      <span className="font-medium text-gray-800">{g.activeCustomerCount ?? g.customerCount ?? "?"}</span> active customers
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Broadcast history panel ── */}
        <div className="lg:w-80 flex-shrink-0">
          <div className="bg-[#e5ddd5] rounded-2xl overflow-hidden h-full" style={{ minHeight: 320 }}>
            {/* Chat header */}
            <div className="bg-[#075e54] text-white px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Radio className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-semibold">
                  {selected ? selected.name : "Broadcast History"}
                </p>
                <p className="text-[11px] text-white/70">
                  {selected ? "Previous broadcasts" : "Select a group to see history"}
                </p>
              </div>
            </div>

            {/* Messages area */}
            <div className="p-3 overflow-y-auto" style={{ maxHeight: 420 }}>
              {!selected ? (
                <div className="flex flex-col items-center justify-center h-40 text-center">
                  <Radio className="w-8 h-8 text-gray-400 opacity-40 mb-2" />
                  <p className="text-xs text-gray-500">Select a group to see broadcast history</p>
                </div>
              ) : historyLoading ? (
                <div className="flex justify-center items-center h-40">
                  <Loader className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : history.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-center">
                  <Send className="w-8 h-8 text-gray-400 opacity-40 mb-2" />
                  <p className="text-xs text-gray-500">No broadcasts to this group yet.</p>
                  <p className="text-[11px] text-gray-400 mt-1">This will be the first one!</p>
                </div>
              ) : (
                history.map((b) => <BroadcastBubble key={b.id} b={b} />)
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepGroup;
